import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft, MessageCircleHeart, Loader2, ShieldCheck, ShieldAlert, Clock,
  Send, Play, CheckCircle2, XCircle, ChevronRight, PlugZap, Unplug, Users, X, TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

function Shot({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objUrl;
    (async () => {
      try {
        const res = await axios.get(`${API}/ops/churn-nudge/sms-screenshot`, { params: { path }, responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      } catch { /* ignore */ }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  if (!url) return <div className="h-32 w-full bg-gray-100 rounded animate-pulse" />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="step" className="h-32 w-full object-cover object-top rounded border border-gray-200 hover:border-cyan-400" /></a>;
}

export default function ChurnNudgeAgent() {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [detail, setDetail] = useState(null);
  const [safeInput, setSafeInput] = useState("");
  const [queueItems, setQueueItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const [o, c, r, qd] = await Promise.all([
        axios.get(`${API}/ops/churn-nudge/agent-overview`),
        axios.get(`${API}/ops/churn-nudge/config`),
        axios.get(`${API}/ops/churn-nudge/runs`, { params: { limit: 25 } }),
        axios.get(`${API}/ops/churn-nudge/queue`),
      ]);
      setOv(o.data); setCfg(c.data); setRuns(r.data.runs || []);
      setSafeInput((c.data.test_owner_ids || []).join(", "));
      setQueueItems(qd.data.items || []);
      axios.get(`${API}/ops/churn-nudge/impact`).then(res => setImpact(res.data)).catch(() => {});
    } catch { toast.error("Failed to load agent"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeQ = async (aid) => {
    try { await axios.delete(`${API}/ops/churn-nudge/queue/${aid}`); toast.success("Removed from queue"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const saveCfg = async (patch, quiet) => {
    try {
      const { data } = await axios.put(`${API}/ops/churn-nudge/config`, patch);
      setCfg(data);
      if (!quiet) toast.success("Saved");
      load();
    } catch { toast.error("Save failed"); }
  };

  const run = async (mode) => {
    if (mode === "live" && !cfg?.live_enabled) { toast.error("Arm live sending first"); return; }
    if (mode === "live" && !window.confirm("Send REAL texts now to every eligible owner on the safe list?")) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/ops/churn-nudge/run`, { mode, confirm: mode === "live" ? "SEND" : undefined });
      if (mode === "live") toast.success(`Live run done — sent ${data.sent}, failed ${data.failed}`, { duration: 7000 });
      else toast.success(`Rehearsal — would text ${data.would_nudge?.length || 0}, skipped ${data.skipped?.length || 0}`, { duration: 7000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed", { duration: 8000 }); }
    finally { setRunning(false); }
  };

  const openDetail = async (runId) => {
    try { const { data } = await axios.get(`${API}/ops/churn-nudge/runs/${runId}`); setDetail(data); }
    catch { toast.error("Could not load run"); }
  };

  const saveSafeList = () => {
    const ids = safeInput.split(",").map(s => s.trim()).filter(Boolean);
    saveCfg({ test_owner_ids: ids });
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-rose-500" /></div>;

  const q = ov?.queue_counts || {};
  const safeAll = (cfg?.test_owner_ids || []).length === 0;

  const queuedByDay = Object.entries(
    (queueItems || []).filter(i => i.status === "queued").reduce((acc, it) => {
      const day = (it.created_at || "").slice(0, 10) || "unknown";
      (acc[day] = acc[day] || []).push(it);
      return acc;
    }, {})
  ).sort((a, b) => b[0].localeCompare(a[0]));

  const dayLabel = (d) => {
    if (d === "unknown") return "Date unknown";
    const dt = new Date(d + "T00:00:00");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((today - dt) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div className="space-y-6" data-testid="churn-nudge-agent-page">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => navigate("/agents")} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800" data-testid="back-to-agents">
          <ArrowLeft className="h-4 w-4" /> All agents
        </button>
        <Link to="/ops?tab=churn" className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-800" data-testid="to-daily-ops-churn">
          Daily Ops · Churn <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
            <MessageCircleHeart className="h-6 w-6 text-rose-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Manrope" }}>Churn Nudge Agent</h1>
            <p className="text-sm text-gray-600 mt-0.5">Wins back lapsed daycare regulars by texting a friendly nudge via Gingr — {cfg?.send_time || "12:00"} ET, weekdays only.</p>
          </div>
        </div>
        {ov?.configured ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 h-fit px-3 py-1.5" data-testid="conn-chip"><PlugZap className="h-3.5 w-3.5 mr-1.5" />Gingr connected</Badge>
        ) : (
          <Badge className="bg-slate-100 text-slate-600 border-slate-200 h-fit px-3 py-1.5" data-testid="conn-chip"><Unplug className="h-3.5 w-3.5 mr-1.5" />No Gingr login</Badge>
        )}
      </div>

      {/* Queue glance */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="agent-queue-counts">
        {[["Queued", q.queued || 0, "text-blue-600"], ["Texted / wk", ov?.stats?.sent_this_week || 0, "text-emerald-600"], ["Sent total", ov?.stats?.sent_total || 0, "text-emerald-600"], ["Snoozed", q.snoozed || 0, "text-amber-600"], ["Excluded", q.excluded || 0, "text-gray-500"]].map(([k, v, c]) => (
          <div key={k} className="rounded-lg bg-white border border-gray-100 py-3 text-center shadow-sm">
            <div className={`text-2xl font-bold ${c}`}>{v}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">{k}</div>
          </div>
        ))}
      </div>

      {/* Queued customers grouped by day */}
      {queuedByDay.length > 0 && (
        <Card data-testid="queued-by-day">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-800">Queued customers by day</div>
              <Link to="/ops?tab=churn" className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" data-testid="to-daily-ops-churn-inline">
                Add / manage in Daily Ops <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="space-y-4">
              {queuedByDay.map(([day, list]) => (
                <div key={day} data-testid={`queued-day-${day}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{dayLabel(day)} · {list.length}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map(it => (
                      <span key={it.animal_id} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-blue-200 px-2.5 py-1 text-xs text-gray-700 shadow-sm" data-testid={`queued-chip-${it.animal_id}`}>
                        <b className="font-semibold">{it.name || "Dog"}</b>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-500">{(it.owner_name || "").split(" ")[0] || `owner ${it.owner_id}`}</span>
                        {it.last_nudged_at && <CheckCircle2 className="h-3 w-3 text-emerald-500" title="Already texted" />}
                        <button onClick={() => removeQ(it.animal_id)} title="Remove from queue" className="text-gray-300 hover:text-red-500 ml-0.5" data-testid={`queued-remove-${it.animal_id}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {impact && impact.texted > 0 && (
        <Card className="border-emerald-100 bg-gradient-to-r from-emerald-50/70 to-white" data-testid="impact-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="h-14 w-14 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <TrendingUp className="h-7 w-7 text-emerald-600" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-emerald-600" data-testid="win-rate">{impact.win_rate}%</span>
                <span className="text-sm text-gray-600">came back after a nudge</span>
              </div>
              <div className="text-sm text-gray-500 ml-auto">
                <b className="text-emerald-600">{impact.came_back}</b> of <b>{impact.texted}</b> texted dogs returned
              </div>
            </div>
            {impact.winners?.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-3" data-testid="impact-winners">
                {impact.winners.map((w, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-200 px-2.5 py-1 text-xs text-gray-700 shadow-sm">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    <b>{w.name || "Dog"}</b>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500">back {new Date(w.returned_on).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Controls */}
        <Card className="border-gray-200">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-gray-900">Safety &amp; schedule</h2>

            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <div>
                <div className="flex items-center gap-1.5 font-medium text-sm text-gray-800">
                  {cfg?.live_enabled ? <ShieldCheck className="h-4 w-4 text-emerald-500" /> : <ShieldAlert className="h-4 w-4 text-amber-500" />}
                  Arm live sending
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Master switch. Off = nothing is ever texted.</p>
              </div>
              <Switch checked={!!cfg?.live_enabled} onCheckedChange={(v) => saveCfg({ live_enabled: v })} data-testid="arm-live-toggle" />
            </div>

            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <div>
                <div className="flex items-center gap-1.5 font-medium text-sm text-gray-800"><Clock className="h-4 w-4 text-gray-500" />Daily auto-run</div>
                <p className="text-xs text-gray-500 mt-0.5">Runs the queue at {cfg?.send_time || "12:00"} ET, Mon–Fri.</p>
              </div>
              <Switch checked={!!cfg?.schedule_enabled} onCheckedChange={(v) => { if (v && !cfg?.live_enabled) { toast.error("Arm live sending first"); return; } saveCfg({ schedule_enabled: v }); }} data-testid="schedule-toggle" />
            </div>

            <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-1.5 font-medium text-sm text-gray-800"><Users className="h-4 w-4 text-gray-500" />Safe list (owner IDs)</div>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">
                {safeAll ? "Empty = texts ALL eligible owners (real customers)." : "Only these Gingr owner IDs get texted. Clear to go wide."}
              </p>
              <div className="flex gap-2">
                <Input value={safeInput} onChange={e => setSafeInput(e.target.value)} placeholder="e.g. 4, 128, 502  (empty = everyone)" className="h-9 text-sm" data-testid="safe-list-input" />
                <Button size="sm" variant="outline" onClick={saveSafeList} data-testid="safe-list-save">Save</Button>
              </div>
              {safeAll && cfg?.live_enabled && (
                <p className="text-[11px] text-rose-600 font-semibold mt-2">⚠ Live + empty safe list = every queued eligible customer will be texted.</p>
              )}
              {!safeAll && cfg?.live_enabled && (
                <p className="text-[11px] text-amber-700 font-semibold mt-2" data-testid="safe-list-restrict-warning">⚠ Safe list is active — ONLY owner IDs {(cfg?.test_owner_ids || []).join(", ")} will be texted. Every other queued dog is skipped. Clear the box to text all eligible customers.</p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 gap-1.5" onClick={() => run("rehearse")} disabled={running} data-testid="run-rehearse-btn">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Rehearse
              </Button>
              <Button className="flex-1 gap-1.5 bg-rose-600 hover:bg-rose-700" onClick={() => run("live")} disabled={running || !cfg?.live_enabled} data-testid="run-live-btn">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Run live now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Message template */}
        <Card className="border-gray-200">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-gray-900">Nudge message</h2>
            <p className="text-xs text-gray-500">Use {"{owner_first}"}, {"{name}"} and {"{location}"} — they fill in per dog.</p>
            <textarea
              className="w-full border rounded-lg p-3 text-sm h-32 resize-none focus:ring-2 focus:ring-rose-200"
              value={cfg?.message_template || ""}
              onChange={e => setCfg({ ...cfg, message_template: e.target.value })}
              data-testid="message-template"
            />
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1.5 text-gray-600">Cooldown
                  <Input type="number" value={cfg?.cooldown_days ?? 7} onChange={e => setCfg({ ...cfg, cooldown_days: parseInt(e.target.value) || 0 })} className="h-8 w-16 text-sm" data-testid="cooldown-input" />d</label>
                <label className="flex items-center gap-1.5 text-gray-600">Max
                  <Input type="number" value={cfg?.max_attempts ?? 3} onChange={e => setCfg({ ...cfg, max_attempts: parseInt(e.target.value) || 0 })} className="h-8 w-16 text-sm" data-testid="maxattempts-input" />x</label>
              </div>
              <Button size="sm" onClick={() => saveCfg({ message_template: cfg.message_template, cooldown_days: cfg.cooldown_days, max_attempts: cfg.max_attempts })} data-testid="save-message-btn">Save</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Run history */}
      <Card className="border-gray-200">
        <CardContent className="p-5">
          <h2 className="font-bold text-gray-900 mb-3">Run history</h2>
          {runs.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No runs yet — hit Rehearse to preview who'd be texted.</p>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="run-history">
              {runs.map((r, i) => (
                <button key={r.run_id || i} onClick={() => r.run_id && openDetail(r.run_id)} className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 rounded-lg px-2" data-testid={`run-row-${i}`}>
                  <div className="flex items-center gap-3">
                    {r.status === "failed" ? <XCircle className="h-4 w-4 text-red-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {r.mode === "live" ? "Live send" : "Rehearsal"}
                        <span className="text-gray-400 font-normal"> · {fmt(r.ran_at)}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.mode === "live" ? <><b className="text-emerald-600">{r.sent || 0}</b> texted{r.failed ? <span className="text-red-500"> · {r.failed} failed</span> : null}{(!r.sent && r.skipped) ? <span className="text-amber-600"> · {r.skipped} skipped</span> : null}</> : <>would text {r.would_nudge || 0}</>}
                        {" · "}<span className="capitalize">{r.trigger}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={r.mode === "live" ? "border-rose-200 text-rose-600" : "border-gray-200 text-gray-500"}>{r.mode}</Badge>
                    {r.run_id && <ChevronRight className="h-4 w-4 text-gray-300" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={() => setDetail(null)} data-testid="run-detail-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold text-gray-900">{detail.mode === "live" ? "Live send" : "Rehearsal"} · {fmt(detail.ran_at)}</h3>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-gray-600">Sent <b className="text-emerald-600">{detail.sent || 0}</b>{detail.failed ? <> · Failed <b className="text-red-500">{detail.failed}</b></> : null}{detail.error ? <span className="text-red-500"> · {detail.error}</span> : null}</div>
              {(detail.results || []).length > 0 && (
                <div className="border rounded-lg divide-y">
                  {detail.results.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {r.sent ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                        <span className="font-medium truncate">{r.name || "Dog"}</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-500 truncate">{r.owner_name}</span>
                      </div>
                      <span className="text-xs text-gray-400 truncate max-w-[45%]">{r.error || r.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {(detail.skipped_detail || []).length > 0 && (
                <div data-testid="run-skipped-section">
                  <div className="text-sm font-semibold text-gray-700 mb-2">
                    Skipped ({detail.skipped_detail.length}) — not texted
                  </div>
                  <div className="border rounded-lg divide-y bg-amber-50/40">
                    {detail.skipped_detail.map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`run-skipped-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <XCircle className="h-4 w-4 text-amber-500 shrink-0" />
                          <span className="font-medium truncate">{s.name || "Dog"}</span>
                          <span className="text-gray-400">·</span>
                          <span className="text-gray-500 truncate">{s.owner_name || `owner ${s.owner_id || "?"}`}</span>
                        </div>
                        <span className="text-xs font-medium text-amber-700 truncate max-w-[45%]">{s.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(detail.screenshots || []).length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Screenshots ({detail.screenshots.length})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {detail.screenshots.map((s, i) => (
                      <div key={i}><Shot path={s.storage_path} /><div className="text-[10px] text-gray-500 text-center mt-1 truncate">{s.owner_id}·{s.name}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
